import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import SearchInput from '../SearchInput/SearchInput';

export default function DebouncedSearchInput({
  value = '',
  onChange,
  onClear,
  debounceMs = 500,
  ...props
}) {
  const [inputState, setInputState] = useState({ draftValue: value, syncedValue: value });
  const timeoutRef = useRef(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const onClearRef = useRef(onClear);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onClearRef.current = onClear;
  }, [onClear]);

  const clearPendingChange = useCallback(() => {
    if (!timeoutRef.current) return;

    clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }, []);

  useLayoutEffect(() => {
    valueRef.current = value;
    clearPendingChange();
  }, [clearPendingChange, value]);

  useEffect(() => clearPendingChange, [clearPendingChange]);

  const emitChange = useCallback((nextValue) => {
    if (onChangeRef.current) {
      onChangeRef.current({ target: { value: nextValue } });
    }
  }, []);

  const resolvedValue = inputState.syncedValue === value ? inputState.draftValue : value;

  const handleChange = useCallback((event) => {
    const nextValue = event.target.value;

    setInputState({ draftValue: nextValue, syncedValue: valueRef.current });
    clearPendingChange();

    if (!onChangeRef.current) return;

    if (debounceMs <= 0) {
      emitChange(nextValue);
      return;
    }

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      emitChange(nextValue);
    }, debounceMs);
  }, [clearPendingChange, debounceMs, emitChange]);

  const handleClear = useCallback(() => {
    clearPendingChange();
    setInputState({ draftValue: '', syncedValue: valueRef.current });

    if (onClearRef.current) {
      onClearRef.current();
      return;
    }

    emitChange('');
  }, [clearPendingChange, emitChange]);

  return (
    <SearchInput
      value={resolvedValue}
      onChange={handleChange}
      onClear={handleClear}
      {...props}
    />
  );
}
